import React from "react";
import { getResultLabel, fmtHMS, fmtDate, fmtDur, R_LABELS, R_COLORS, normalizeResult, SITUATIONS_MAP, getMillis } from "./helpers";
import { Sheet } from "./DispatchModal";
import { getReviewState, isRelevantEvent, REVIEW_LABELS, REVIEW_COLORS } from "../utils/review";

export function DField({ label, value, full, warn }) {
  return (
    <div style={{ gridColumn: full ? "1/-1" : undefined, marginBottom: full ? 6 : 0 }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", marginBottom: 1 }}>{label}</div>
      <div style={{ fontSize: 12, color: warn ? "#fbbf24" : "var(--text)", lineHeight: 1.4 }}>{value}</div>
    </div>
  );
}

/**
 * EventDetail — detalle de un evento (solo lectura).
 * Props opcionales de revisión (Fase 2, solo capa supervisora — la vista
 * operador no las pasa y no ve nada de esto):
 *   reviewRoles: { canReview, canValidate }
 *   onConfirm(alarm), onValidate(alarm), onEdit(alarm), onGuide()
 */
export default function EventDetail({ alarm, onClose, reviewRoles, onConfirm, onValidate, onEdit, onGuide, onCaseReport }) {
  const createdMs  = alarm.createdAt?.seconds  ? alarm.createdAt.seconds  * 1000 : null;
  const closedMs   = alarm.resolvedAt?.seconds  ? alarm.resolvedAt.seconds  * 1000 : null;
  const dispatchMs = alarm.dispatchTime ? getMillis(alarm.dispatchTime) : null;
  const arrivedMs  = alarm.arrivedTime  ? getMillis(alarm.arrivedTime)  : null;

  const dispatchDelta = dispatchMs && createdMs  ? Math.max(0, Math.floor((dispatchMs - createdMs)  / 1000)) : null;
  const arrivalDelta  = arrivedMs  && dispatchMs ? Math.max(0, Math.floor((arrivedMs  - dispatchMs) / 1000)) : null;

  const resultKey = normalizeResult(alarm.closeResult || alarm.reasonForResolution);

  return (
    <Sheet onClose={onClose}>
      <div style={{ fontSize: 15, fontWeight: 800, color: "var(--bright)", marginBottom: 4 }}>Detalle de evento</div>
      {alarm.docID && (
        <div style={{
          fontFamily: "var(--mono)", fontSize: 11, color: "#3b82f6", marginBottom: 12,
          background: "var(--bg)", display: "inline-block", padding: "2px 8px", borderRadius: 5,
        }}>ID: {alarm.docID}</div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <DField label="Zona"              value={alarm.zoneName || "—"} />
        <DField label="Inicio"            value={createdMs ? `${fmtDate(createdMs)} ${fmtHMS(createdMs)}` : "—"} />
        <DField label="Duración ataque"   value={alarm.duration != null ? fmtDur(alarm.duration) : "—"} />
        <DField label="Despachado por"    value={alarm.dispatchedBy || alarm.guardName || "—"} />
        <DField label="T. despacho CCTV"  value={dispatchDelta != null ? fmtDur(dispatchDelta) : "—"} />
        <DField label="T. llegada guardia" value={arrivalDelta != null ? fmtDur(arrivalDelta) : "—"} />
      </div>

      {alarm.resolvedAt && (
        <div style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--subtle)" }}>Resultado:</span>
            {resultKey && (
              <span style={{
                padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                background: `${R_COLORS[resultKey] || "#64748b"}20`,
                color: R_COLORS[resultKey] || "#64748b",
              }}>{R_LABELS[resultKey] || getResultLabel(alarm)}</span>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <DField label="Guardia(s)" value={
              (alarm.guards?.length ? alarm.guards.join(", ") : alarm.guardName) || "—"
            } />
            <DField label="Cerrado por" value={alarm.closedBy || alarm.guardName || "—"} />
            <DField label="Barras señal" value={
              alarm.signalBarsNum ? `${alarm.signalBarsNum} barras` :
              alarm.signalBars && alarm.signalBars !== "not_specified" ? `${alarm.signalBars} barras` : "—"
            } />
            <DField label="Cerrado a las" value={closedMs ? fmtHMS(closedMs) : "—"} />
          </div>
          {alarm.situation && SITUATIONS_MAP[alarm.situation] && (
            <DField label="Situación Encontrada" value={SITUATIONS_MAP[alarm.situation]} full />
          )}
          {alarm.vehicle && <DField label="Vehículo" value={`${alarm.vehicle.plate || ""} — ${alarm.vehicle.desc || ""}`} full />}
          {alarm.plate && !alarm.vehicle && <DField label="Patente" value={alarm.plate} full />}
          {alarm.suspect && alarm.suspectDesc && <DField label="Sospechoso" value={alarm.suspectDesc} full />}
          {(alarm.notes || alarm.comments) && <DField label="Comentarios" value={alarm.notes || alarm.comments} full />}
          {alarm.lateReason && <DField label="Motivo gestión tardía" value={alarm.lateReason} full warn />}
          {alarm.quickCloseReason && <DField label="Motivo cierre rápido" value={alarm.quickCloseReason} full warn />}
        </div>
      )}

      {/* ── Revisión / validación (solo capa supervisora) ── */}
      {reviewRoles?.canReview && alarm.resolvedAt && (() => {
        const state = getReviewState(alarm);
        const color = REVIEW_COLORS[state] || "#64748b";
        const reviewedAtMs = alarm.reviewedAt?.seconds ? alarm.reviewedAt.seconds * 1000 : null;
        const validatedAtMs = alarm.validatedAt?.seconds ? alarm.validatedAt.seconds * 1000 : null;
        const relevant = isRelevantEvent(alarm);
        const abtn = (bg, fg, border) => ({
          padding: "7px 12px", borderRadius: 7, border: `1px solid ${border || bg}`,
          background: bg, color: fg, fontSize: 11, fontWeight: 700, cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 5,
        });
        return (
          <div style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 4 }}>
            {/* Sello de estado */}
            <div style={{
              padding: "6px 10px", borderRadius: 7, marginBottom: 10,
              background: `${color}12`, border: `1px solid ${color}35`,
              fontSize: 10, color, lineHeight: 1.5,
            }}>
              <b>{state === "validated" ? "🛡 " : ""}{REVIEW_LABELS[state]}</b>
              {state === "validated" && alarm.caseFolio && (
                <span style={{ fontFamily: "var(--mono)", marginLeft: 6, opacity: .85 }}>· {alarm.caseFolio}</span>
              )}
              {state === "validated" && alarm.validatedBy && (
                <> · por {alarm.validatedBy}{validatedAtMs ? ` · ${fmtDate(validatedAtMs)} ${fmtHMS(validatedAtMs)}` : ""}
                {alarm.reviewedBy && alarm.reviewedBy !== alarm.validatedBy ? ` · revisado antes por ${alarm.reviewedBy}` : ""}</>
              )}
              {(state === "confirmed" || state === "edited") && alarm.reviewedBy && (
                <> · por {alarm.reviewedBy}{reviewedAtMs ? ` · ${fmtDate(reviewedAtMs)} ${fmtHMS(reviewedAtMs)}` : ""}</>
              )}
              {state === "pending" && <> — pendiente de revisión del supervisor.</>}
            </div>

            {/* Barra de acciones */}
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
              {state !== "validated" && (
                <button type="button" onClick={() => onConfirm && onConfirm(alarm)} style={abtn("#3b82f6", "#071022")}>
                  ✓ Confirmar
                </button>
              )}
              {onEdit && (
                <button type="button" onClick={() => onEdit(alarm)} style={abtn("#f59e0b22", "#fbbf24", "#f59e0b55")}>
                  ✎ Corregir…
                </button>
              )}
              {reviewRoles?.canValidate && relevant && state !== "validated" && (
                <button type="button" onClick={() => onValidate && onValidate(alarm)} style={abtn("#22c55e", "#08130b")}>
                  🛡 Validar caso
                </button>
              )}
              {state === "validated" && onCaseReport && (
                <button type="button" onClick={() => onCaseReport(alarm)} style={abtn("var(--surface)", "var(--bright)", "var(--border)")}>
                  📄 Ficha del caso
                </button>
              )}
              <span style={{ flex: 1 }} />
              {onGuide && (
                <button type="button" onClick={onGuide} style={abtn("transparent", "var(--muted)", "var(--border)")}>
                  ? Guía
                </button>
              )}
            </div>
            {reviewRoles?.canValidate && !relevant && state !== "validated" && (
              <div style={{ fontSize: 9, color: "var(--dim)", marginTop: 6 }}>
                Validar está disponible solo en eventos relevantes (hallazgo, patente o sospechoso).
              </div>
            )}
          </div>
        );
      })()}
    </Sheet>
  );
}
